import React from 'react';
import { Spin } from 'antd';
import { LoadingOutlined } from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';

// Custom Icons
const VinIcon = () => (
  <svg className="spec-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
    <rect x="3" y="4" width="18" height="16" rx="2" />
    <path d="M7 8h10M7 12h6M7 16h8" strokeLinecap="round" />
  </svg>
);

const CalendarIcon = () => (
  <svg className="spec-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
    <rect x="3" y="4" width="18" height="18" rx="2" />
    <path d="M16 2v4M8 2v4M3 10h18" strokeLinecap="round" />
  </svg>
);

const TagIcon = () => (
  <svg className="spec-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
    <path d="M7 7h.01M7 3h5l9 9-7 7-9-9V3z" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);

const GaugeIcon = () => (
  <svg className="spec-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
    <circle cx="12" cy="12" r="10" />
    <path d="M12 6v2M6 12h2M18 12h-2M12 12l3-3" strokeLinecap="round" />
  </svg>
);

const CarIcon = () => (
  <svg className="spec-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
    <path d="M5 17h14M5 17a2 2 0 01-2-2v-4a1 1 0 011-1h1l2-4h10l2 4h1a1 1 0 011 1v4a2 2 0 01-2 2M5 17v2M19 17v2" />
    <circle cx="7" cy="14" r="1" />
    <circle cx="17" cy="14" r="1" />
  </svg>
);

const ModelIcon = () => (
  <svg className="spec-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
    <rect x="2" y="6" width="20" height="12" rx="2" />
    <circle cx="7" cy="18" r="2" />
    <circle cx="17" cy="18" r="2" />
    <path d="M5 6l2-3h10l2 3" />
  </svg>
);

const DealerIcon = () => (
  <svg className="spec-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
    <path d="M3 21h18M5 21V7l7-4 7 4v14M9 21v-6h6v6" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);

const LocationIcon = () => (
  <svg className="spec-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
    <path d="M12 21s-8-7.5-8-12a8 8 0 1116 0c0 4.5-8 12-8 12z" />
    <circle cx="12" cy="9" r="3" />
  </svg>
);

const Summary = ({ loading, vin, trimValue, carDetail }) => {
  const navigate = useNavigate();

  const specs = [
    { icon: <VinIcon />, label: 'VIN', value: vin?.toUpperCase() },
    { icon: <CalendarIcon />, label: 'Year', value: carDetail?.year },
    { icon: <TagIcon />, label: 'Trim', value: carDetail?.trim || trimValue || '-' },
    { icon: <GaugeIcon />, label: 'Mileage', value: carDetail?.mileage },
    { icon: <CarIcon />, label: 'Make', value: carDetail?.make || carDetail?.manufacturer },
    { icon: <ModelIcon />, label: 'Model', value: carDetail?.model },
    { icon: <DealerIcon />, label: 'Dealer', value: carDetail?.dealerName || carDetail?.seller_name },
    { icon: <LocationIcon />, label: 'Location', value: `${carDetail?.city || ''}, ${carDetail?.state || ''}` },
  ];

  return (
    <Spin
      spinning={loading}
      indicator={
        <LoadingOutlined
          style={{ fontSize: 48, color: '#38bdf8' }}
          spin
        />
      }
    >
      <div className="product-summary">
        <div className="product-summary-container">
          {/* Section Header */}
          <div className="product-section-header">
            <div className="product-section-icon">
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
                <path d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2" />
                <rect x="9" y="3" width="6" height="4" rx="1" />
                <path d="M9 12h6M9 16h6" strokeLinecap="round" />
              </svg>
            </div>
            <div>
              <h2 className="product-section-title">Vehicle Specifications</h2>
              <p className="product-section-subtitle">Complete details and information</p>
            </div>
          </div>

          {/* Specs Grid */}
          <div className="product-specs-grid">
            {specs.map((spec, index) => (
              <div key={index} className="product-spec-card">
                <div className="product-spec-icon">
                  {spec.icon}
                </div>
                <div className="product-spec-content">
                  <span className="product-spec-label">{spec.label}</span>
                  <span className="product-spec-value">{spec.value || '-'}</span>
                </div>
              </div>
            ))}
          </div>

          {/* Quick Actions */}
          <div className="product-actions">
            <button
              className="product-action-btn product-action-primary"
              onClick={() => navigate('/generatePage')}
            >
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M9 17v-2m3 2v-4m3 4v-6m2 10H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" strokeLinecap="round" strokeLinejoin="round" />
              </svg>
              Generate Report
            </button>
            <button
              className="product-action-btn product-action-secondary"
              onClick={() => navigate('/form_page')}
            >
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" strokeLinecap="round" strokeLinejoin="round" />
              </svg>
              New Search
            </button>
          </div>
        </div>
      </div>
    </Spin>
  );
};

export default Summary;
